// The emoji picker: the emoji the user took last, then Unicode's groups — a tab each — and a search by
// their Russian names and keywords and by their shortcodes. A click takes an emoji and closes the picker
// (with Shift, it stays open for more); Escape or a click elsewhere closes it.
import { useEffect, useRef, useState } from "preact/hooks";
import { emojiGroups, recentEmoji, searchEmoji, type Emoji } from "../emoji.ts";

export function EmojiPicker({
  onPick,
  onClose,
  place = "above",
}: {
  onPick: (emoji: string) => void;
  onClose: () => void;
  place?: "above" | "below";
}) {
  const [query, setQuery] = useState("");
  const [hover, setHover] = useState<Emoji | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const groups = emojiGroups();
  // The search takes the keys at once (Preact does not focus `autoFocus` of what comes later).
  useEffect(() => search.current?.focus(), []);
  useEffect(() => {
    const down = (e: PointerEvent) => {
      if (box.current !== null && !box.current.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("keydown", key, true);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("keydown", key, true);
    };
  }, [onClose]);
  const pick = (e: string, keep: boolean) => {
    onPick(e);
    if (!keep) onClose();
  };
  const cell = (x: Emoji | string) => {
    const e = typeof x === "string" ? x : x.e;
    return (
      <button
        key={e}
        class="emoji-cell"
        title={typeof x === "string" ? e : x.name}
        onMouseEnter={() => setHover(typeof x === "string" ? null : x)}
        onClick={(ev) => pick(e, ev.shiftKey)}
      >
        {e}
      </button>
    );
  };
  const found = query.trim() === "" ? null : searchEmoji(query, 160);
  const recent = recentEmoji();
  const go = (key: string) => {
    const g = grid.current?.querySelector(`[data-group="${key}"]`);
    if (g instanceof HTMLElement && grid.current !== null) grid.current.scrollTop = g.offsetTop - grid.current.offsetTop;
  };
  return (
    <div class={`emoji-picker ${place}`} ref={box} onClick={(e) => e.stopPropagation()}>
      <input
        ref={search}
        class="emoji-search"
        placeholder="Найти эмодзи"
        value={query}
        onInput={(e) => setQuery(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && found !== null && found.length > 0) pick(found[0].e, e.shiftKey);
        }}
      />
      {found === null && groups !== null && (
        <nav class="emoji-tabs">
          <button title="Недавние" onClick={() => go("recent")}>
            🕘
          </button>
          {groups.map((g) => (
            <button key={g.key} title={g.name} onClick={() => go(g.key)}>
              {g.emoji[0]?.e}
            </button>
          ))}
        </nav>
      )}
      <div class="emoji-scroll" ref={grid}>
        {groups === null ? (
          <div class="empty">загружаю…</div>
        ) : found !== null ? (
          found.length === 0 ? (
            <div class="empty">ничего не нашлось</div>
          ) : (
            <div class="emoji-grid">{found.map(cell)}</div>
          )
        ) : (
          <>
            <div class="emoji-group" data-group="recent">
              Недавние
            </div>
            <div class="emoji-grid">{recent.map(cell)}</div>
            {groups.map((g) => (
              <div key={g.key}>
                <div class="emoji-group" data-group={g.key}>
                  {g.name}
                </div>
                <div class="emoji-grid">{g.emoji.map(cell)}</div>
              </div>
            ))}
          </>
        )}
      </div>
      <div class="emoji-foot">
        {hover === null ? (
          <span class="muted">Shift — выбрать несколько</span>
        ) : (
          <>
            <span class="emoji-big">{hover.e}</span>
            <span class="emoji-name">{hover.name}</span>
            {hover.codes !== "" && <code>:{hover.codes.split(" ")[0]}:</code>}
          </>
        )}
      </div>
    </div>
  );
}
